import React, { useState, useMemo } from 'react';
import './Table.scss';

export interface TableColumn {
  field: string;
  headerName: string;
  minWidth?: number;
  maxWidth?: number;
  flex?: number;
  sortable?: boolean;
  renderCell?: (params: { row: any; id: any; value: any }) => React.ReactNode;
}

interface TableProps {
  rows: any[];
  columns: TableColumn[];
  pageSize?: number;
  showSearch?: boolean;
  showPagination?: boolean;
}

const Table: React.FC<TableProps> = ({ 
  rows, 
  columns, 
  pageSize = 10,
  showSearch = true,
  showPagination = true
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);

  // Filtrage
  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    
    return rows.filter(row => {
      return columns.some(column => {
        const value = row[column.field];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchQuery.toLowerCase());
      });
    });
  }, [rows, searchQuery, columns]);

  // Tri
  const sortedRows = useMemo(() => {
    if (!sortConfig) return filteredRows;

    const sorted = [...filteredRows].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredRows, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedRows.length / pageSize);
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedRows.slice(startIndex, endIndex);
  }, [sortedRows, currentPage, pageSize]);

  const handleSort = (field: string) => {
    const column = columns.find(col => col.field === field);
    if (column?.sortable === false) return;

    setSortConfig(current => {
      if (!current || current.field !== field) {
        return { field, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { field, direction: 'desc' };
      }
      return null;
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="custom-table">
      {showSearch && (
        <div className="custom-table__toolbar">
          <div className="custom-table__search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="custom-table__info">
            {filteredRows.length} résultat{filteredRows.length > 1 ? 's' : ''}
          </div>
        </div>
      )}

      <div className="custom-table__container">
        <table className="custom-table__table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.field}
                  style={{
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    width: column.flex ? `${column.flex * 100}%` : 'auto'
                  }}
                  className={column.sortable !== false ? 'sortable' : ''}
                  onClick={() => handleSort(column.field)}
                >
                  <div className="custom-table__header-cell">
                    <span>{column.headerName}</span>
                    {column.sortable !== false && (
                      <div className="custom-table__sort-icon">
                        {sortConfig?.field === column.field ? (
                          sortConfig.direction === 'asc' ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="18 15 12 9 6 15"></polyline>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          )
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.3">
                            <polyline points="18 15 12 9 6 15"></polyline>
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="custom-table__empty">
                  Aucun résultat trouvé
                </td>
              </tr>
            ) : (
              paginatedRows.map((row, rowIndex) => (
                <tr key={row.id || rowIndex}>
                  {columns.map((column) => (
                    <td key={column.field}>
                      {column.renderCell ? (
                        column.renderCell({ 
                          row, 
                          id: row.id, 
                          value: row[column.field] 
                        })
                      ) : (
                        row[column.field]
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPagination && totalPages > 1 && (
        <div className="custom-table__pagination">
          <button
            className="custom-table__pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <div className="custom-table__pagination-numbers">
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="custom-table__pagination-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  className={`custom-table__pagination-number ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page as number)}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          <button
            className="custom-table__pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default Table;
